import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import type { ReviewFormData } from "@/components/ReviewForm";
import type { Review } from "@/types/review";

export const useReviewActions = (
  review: Review | null,
  onComplete?: () => void
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [userVote, setUserVote] = useState<"up" | "down" | null>(null);
  const [upvotes, setUpvotes] = useState(review?.upvotes || 0);
  const [downvotes, setDownvotes] = useState(review?.downvotes || 0);
  const [isVoting, setIsVoting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReported, setIsReported] = useState(review?.reported || false);

  const checkAuth = async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      toast.error("Please sign in to perform this action");
      navigate("/auth");
      return false;
    }
    return true;
  };

  const handleDelete = async () => {
    if (!await checkAuth()) return;
    
    if (!review?.id) {
      toast.error("Review not found");
      return;
    }

    if (window.confirm(t('confirm_delete_review') || "Are you sure you want to delete this review?")) {
      setIsDeleting(true);
      try {
        const { error } = await supabase
          .from('reviews')
          .delete()
          .eq('id', review.id);

        if (error) throw error;

        toast.success(t('review_deleted_success') || "Review deleted successfully");
        
        if (onComplete) {
          onComplete();
        }
      } catch (error: any) {
        toast.error(error.message || "Failed to delete review");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleReport = async (reason: string) => {
    if (!await checkAuth()) return;
    
    if (!review?.id) {
      toast.error("Review not found");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to report a review");
        return;
      }

      const { error } = await supabase
        .from('review_reports')
        .insert({
          review_id: review.id,
          reporter_id: user.id,
          reason: reason
        });

      if (error) throw error;

      setIsReported(true);
      toast.success(t('review_reported_success') || "Review reported successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to report review");
    }
  };

  const handleDismissReport = async () => {
    if (!await checkAuth()) return;
    
    if (!review?.id) {
      toast.error("Review not found");
      return;
    }

    try {
      const { error } = await supabase
        .from('review_reports')
        .delete()
        .eq('review_id', review.id);

      if (error) throw error;

      setIsReported(false);
      toast.success(t('report_dismissed_success') || "Report dismissed successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to dismiss report");
    }
  };

  const handleUpvote = async () => {
    if (!await checkAuth()) return;
    
    if (!review?.id) {
      toast.error("Review not found");
      return;
    }

    try {
      setIsVoting(true);
      
      if (userVote === "up") {
        await supabase
          .from('review_votes')
          .delete()
          .eq('review_id', review.id);
          
        setUserVote(null);
        setUpvotes(prevUpvotes => Math.max(0, prevUpvotes - 1));
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          toast.error("You must be logged in to vote");
          return;
        }

        await supabase
          .from('review_votes')
          .delete()
          .eq('review_id', review.id)
          .eq('user_id', user.id);

        await supabase
          .from('review_votes')
          .insert({
            review_id: review.id,
            user_id: user.id,
            vote_type: 'upvote'
          });

        setUserVote("up");
        setUpvotes(prevUpvotes => prevUpvotes + 1);
        
        if (userVote === "down") {
          setDownvotes(prevDownvotes => Math.max(0, prevDownvotes - 1));
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to upvote review");
    } finally {
      setIsVoting(false);
    }
  };

  const handleDownvote = async () => {
    if (!await checkAuth()) return;
    
    if (!review?.id) {
      toast.error("Review not found");
      return;
    }

    try {
      setIsVoting(true);
      
      if (userVote === "down") {
        await supabase
          .from('review_votes')
          .delete()
          .eq('review_id', review.id);
          
        setUserVote(null);
        setDownvotes(prevDownvotes => Math.max(0, prevDownvotes - 1));
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          toast.error("You must be logged in to vote");
          return;
        }

        await supabase
          .from('review_votes')
          .delete()
          .eq('review_id', review.id)
          .eq('user_id', user.id);

        await supabase
          .from('review_votes')
          .insert({
            review_id: review.id,
            user_id: user.id,
            vote_type: 'downvote'
          });

        setUserVote("down");
        setDownvotes(prevDownvotes => prevDownvotes + 1);
        
        if (userVote === "up") {
          setUpvotes(prevUpvotes => Math.max(0, prevUpvotes - 1));
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to downvote review");
    } finally {
      setIsVoting(false);
    }
  };

  const handleSubmitEdit = async (formData: ReviewFormData) => {
    if (!await checkAuth()) return;
    
    if (!review?.id) {
      toast.error("Review not found");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .update({
          airport_name: formData.airport_name,
          airport_location: formData.airport_location,
          review_text: formData.review_text,
          film_friendly_score: formData.film_friendly_score,
          hand_check_success: formData.hand_check_success,
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null,
          terminal: formData.terminal || null,
          hand_check_date: formData.hand_check_date || review.hand_check_date
        })
        .eq('id', review.id);

      if (error) throw error;

      toast.success(t('review_updated_success') || "Review updated successfully");
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to update review");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isDeleting,
    userVote,
    upvotes,
    downvotes,
    isVoting,
    isEditing,
    isSubmitting,
    isReported,
    setIsEditing,
    setUserVote,
    handleDelete,
    handleReport,
    handleDismissReport,
    handleUpvote,
    handleDownvote,
    handleSubmitEdit
  };
};
